import type { EquipmentStatus } from './equipment';

// ============================================================================
// Equipment status state machine — AAMI ST79 sterile processing workflow
// ----------------------------------------------------------------------------
// Sterilizable items follow the full ST79 cycle:
//
//   available → in-use → soiled → awaiting-decontamination →
//   in-decontamination → clean-and-inspected → in-sterilization →
//   sterile-ready → available
//
// Plus off-cycle transitions (any → maintenance, out-of-service, retired)
// and limited recovery paths (out-of-service → available/maintenance/retired).
//
// Non-sterilizable items (C-arm, OR table, ventilator) skip the cycle states.
// Their workflow is much simpler:
//
//   available ↔ in-use, plus the off-cycle states.
//
// The detail page passes `sterilization_required` into getAllowedTransitions
// to pick the right workflow per item. This avoids forcing a materials manager
// to think "should I send the C-arm to decontamination?" — the option just
// doesn't appear.
//
// `requires_reason` flags transitions that need a free-text justification for
// compliance/audit purposes (out-of-service, retire, sterilization failures).
// `side_effect` notes are surfaced as warnings in the edit form.
// ============================================================================

export interface StatusTransition {
    to: EquipmentStatus;
    label: string;
    requires_reason?: boolean;
    side_effect?: string;
}

// ----------------------------------------------------------------------------
// Sterilizable workflow — full AAMI ST79 cycle
// ----------------------------------------------------------------------------
const STERILIZABLE_TRANSITIONS: Record<EquipmentStatus, StatusTransition[]> = {
    'available': [
        { to: 'in-use', label: 'Mark in use' },
        { to: 'maintenance', label: 'Send for maintenance' },
        {
            to: 'out-of-service',
            label: 'Mark out of service',
            requires_reason: true
        },
        {
            to: 'retired',
            label: 'Retire',
            requires_reason: true,
            side_effect: 'Retiring is permanent in v1 — only an admin can reverse this.'
        }
    ],
    'in-use': [
        { to: 'soiled', label: 'Case complete (mark soiled)' },
        {
            to: 'out-of-service',
            label: 'Failed mid-case',
            requires_reason: true
        }
    ],
    'soiled': [
        {
            to: 'awaiting-decontamination',
            label: 'Transport to decontam area',
            side_effect: 'Confirms point-of-use cleaning was completed in the OR.'
        }
    ],
    'awaiting-decontamination': [
        { to: 'in-decontamination', label: 'Begin decontamination' }
    ],
    'in-decontamination': [
        {
            to: 'clean-and-inspected',
            label: 'Decontamination complete'
        },
        {
            to: 'out-of-service',
            label: 'Failed inspection — damaged',
            requires_reason: true
        }
    ],
    'clean-and-inspected': [
        { to: 'in-sterilization', label: 'Send to sterilization' }
    ],
    'in-sterilization': [
        { to: 'sterile-ready', label: 'Sterilization passed' },
        {
            to: 'awaiting-decontamination',
            label: 'Failed BI — reprocess',
            requires_reason: true,
            side_effect: 'Required for biological indicator failures. Returns item to decontam.'
        },
        {
            to: 'out-of-service',
            label: 'Failed sterilization (cannot reprocess)',
            requires_reason: true
        }
    ],
    'sterile-ready': [
        { to: 'available', label: 'Stocked in clean storage' },
        { to: 'in-use', label: 'Pulled for case' }
    ],
    'maintenance': [
        {
            to: 'available',
            label: 'Maintenance complete',
            side_effect: 'Will auto-compute next maintenance date from interval.'
        },
        {
            to: 'out-of-service',
            label: 'Cannot be repaired',
            requires_reason: true
        }
    ],
    'out-of-service': [
        { to: 'available', label: 'Returned to service' },
        { to: 'maintenance', label: 'Under repair' },
        {
            to: 'retired',
            label: 'Retire (total loss)',
            requires_reason: true
        }
    ],
    'retired': []
};

// ----------------------------------------------------------------------------
// Non-sterilizable workflow — simplified (no cycle states)
// ----------------------------------------------------------------------------
const NON_STERILIZABLE_TRANSITIONS: Record<EquipmentStatus, StatusTransition[]> = {
    'available': [
        { to: 'in-use', label: 'Mark in use' },
        { to: 'maintenance', label: 'Send for maintenance' },
        {
            to: 'out-of-service',
            label: 'Mark out of service',
            requires_reason: true
        },
        {
            to: 'retired',
            label: 'Retire',
            requires_reason: true,
            side_effect: 'Retiring is permanent in v1 — only an admin can reverse this.'
        }
    ],
    'in-use': [
        { to: 'available', label: 'Return to available' },
        {
            to: 'out-of-service',
            label: 'Failed mid-case',
            requires_reason: true
        }
    ],
    'maintenance': [
        {
            to: 'available',
            label: 'Maintenance complete',
            side_effect: 'Will auto-compute next maintenance date from interval.'
        },
        {
            to: 'out-of-service',
            label: 'Cannot be repaired',
            requires_reason: true
        }
    ],
    'out-of-service': [
        { to: 'available', label: 'Returned to service' },
        { to: 'maintenance', label: 'Under repair' },
        {
            to: 'retired',
            label: 'Retire (total loss)',
            requires_reason: true
        }
    ],
    'retired': [],

    // States below should never be reached on a non-sterilizable item, but we
    // include them defensively. If a row ends up in one of these states (e.g.
    // historical data, bug, manual SQL edit), the user can recover it via
    // the same off-cycle paths as the rest.
    'soiled': [
        { to: 'available', label: 'Mark clean' },
        {
            to: 'out-of-service',
            label: 'Mark out of service',
            requires_reason: true
        }
    ],
    'awaiting-decontamination': [
        { to: 'available', label: 'Skip decon — return to available' }
    ],
    'in-decontamination': [
        { to: 'available', label: 'Skip cycle — return to available' }
    ],
    'clean-and-inspected': [
        { to: 'available', label: 'Skip sterilization — return to available' }
    ],
    'in-sterilization': [
        { to: 'available', label: 'Sterilization complete' }
    ],
    'sterile-ready': [
        { to: 'available', label: 'Return to available' }
    ]
};

// ----------------------------------------------------------------------------
// Public API
// ----------------------------------------------------------------------------

/**
 * Returns the valid transitions out of `from` for an item.
 * Pass the item's sterilization_required flag to pick the workflow.
 */
export function getAllowedTransitions(
    from: EquipmentStatus,
    sterilizationRequired: boolean | null | undefined
): StatusTransition[] {
    const map = sterilizationRequired === true
        ? STERILIZABLE_TRANSITIONS
        : NON_STERILIZABLE_TRANSITIONS;
    return map[from] ?? [];
}

/** Find the descriptor for a specific from→to transition, or null. */
export function getTransition(
    from: EquipmentStatus,
    to: EquipmentStatus,
    sterilizationRequired: boolean | null | undefined
): StatusTransition | null {
    if (from === to) return null;
    return getAllowedTransitions(from, sterilizationRequired)
        .find((t) => t.to === to) ?? null;
}

/** Whether a given transition is allowed for the item. */
export function isValidTransition(
    from: EquipmentStatus,
    to: EquipmentStatus,
    sterilizationRequired: boolean | null | undefined
): boolean {
    if (from === to) return true;
    return getTransition(from, to, sterilizationRequired) !== null;
}
